import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouteChange } from '@/hooks/useRouteChange';
import { ROUTE_FROM } from '@/utils/const';
import { PageVisitFrom } from '@/utils/eventlog/eventNewConfig';
import { t } from '@/utils/helper/intl';
import { aiToolURL, homeURL, libraryURL } from '@/utils/router';

import {
  AIToolsIcon,
  AIToolsSelectedIcon,
  CreativeLibrary,
  CreativeLibrarySelectedIcon,
  HomeIcon,
  HomeSelectedIcon,
} from '../../icons';

type MenuInfo = {
  key: string;
};
export interface MenuItem {
  label: string;
  key?: string;
  icon?: React.ReactNode;
  type?: 'group';
  children?: MenuItem[];
}

const MenuIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="left-menu-icon-frame">{children}</span>
);

export const createMenuList = (selectedKey = ''): MenuItem[] => {
  return [
    {
      label: t('db1c89e0f6e62f9642018cbb531bbd4f'),
      key: homeURL,
      icon: (
        <MenuIcon>
          {selectedKey === homeURL ? <HomeSelectedIcon /> : <HomeIcon />}
        </MenuIcon>
      ),
    },
    {
      label: t('fc.aitools'),
      key: aiToolURL,
      icon: (
        <MenuIcon>
          {selectedKey === aiToolURL ? <AIToolsSelectedIcon /> : <AIToolsIcon />}
        </MenuIcon>
      ),
    },
    {
      label: t('5e0fcc8fa4c3b0f1c95d2a294a61cc1e'),
      key: libraryURL,
      icon: (
        <MenuIcon>
          {selectedKey === libraryURL ? (
            <CreativeLibrarySelectedIcon />
          ) : (
            <CreativeLibrary />
          )}
        </MenuIcon>
      ),
    },
  ];
};

// 功能页面 → 左侧菜单高亮 key 的映射
// 未列出的路径默认用 pathname 本身匹配
export const PATHNAME_TO_MENU_KEY: Record<string, string> = {
  // Home 下的功能页（金刚位入口）
  '/ai-video-generator': homeURL,
  '/ai-image-generator': homeURL,
  '/ai-viral-template': homeURL,
  '/ai-viral-template/[templateId]': homeURL,
  '/ai-viral-template/workbench/[templateId]': homeURL,
  '/ai-avatar-generator': homeURL,
  '/ai-product-avatar': homeURL,
  '/ai-viral-remake': homeURL,
  // AI Tools 下的工具页
  '/video-translator': aiToolURL,
  '/ai-image-translator': aiToolURL,
  '/face-swap': aiToolURL,
  '/ai-voice-cloning': aiToolURL,
  '/ai-image-enhancer': aiToolURL,
  '/ai-video-editor': aiToolURL,
  '/video-subtitle-remover': aiToolURL,
};

export const useRouterMenu = () => {
  const { navigate } = useRouteChange();
  const router = useRouter();

  const [selectedKeys, setSelectedKeys] = useState(['']);
  const selectedKey = selectedKeys[0] ?? '';
  const menuList = useMemo(() => createMenuList(selectedKey), [selectedKey]);

  useEffect(() => {
    setSelectedKeys([
      PATHNAME_TO_MENU_KEY[router.pathname] ?? router.pathname ?? homeURL,
    ]);
  }, [router.pathname]);

  const jump = useCallback(
    (menuInfo: MenuInfo) => {
      const { key } = menuInfo;
      // sessionStorage.setItem(ROUTE_FROM, PageVisitFrom.SIDEBAR);
      navigate(() => {
        router.replace(key);
      });
    },
    [navigate, router],
  );

  return {
    selectedKeys,
    menuList,
    jump,
  };
};
